import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { fitText } from './utils/fit-text';
import { initCursorFollowClose } from './utils/cursor-follow-close';

// Generic slide-out drawer. Reusable across sections (hero, collection promo, …).
// Each instance carries `data-drawer-id`; triggers carry `data-cta-type="open_drawer"`
// + `data-drawer-target="<id>"`. A single delegated click listener (bottom of file)
// dispatches `drawer:open` with the target id, and only the matching instance opens.
// This supports multiple independent drawers on the same page.
initComponent('.js-drawer', (drawer) => {
  const drawerId = drawer.dataset.drawerId;
  const panel = drawer.querySelector('.js-drawer__panel') as HTMLElement;
  const overlay = drawer.querySelector('.js-drawer__overlay') as HTMLElement;
  const closeBtn = drawer.querySelector('[data-cta-type="close_drawer"]') as HTMLElement;
  const drawerScroll = drawer.querySelector('.js-drawer__scroll') as HTMLElement;
  const header = document.querySelector('.js-header') as HTMLElement | null;

  // Close icon that follows the cursor over the overlay (md+).
  const closeFollow = drawer.querySelector('.js-drawer-close-follow') as HTMLElement | null;
  const cursorClose = initCursorFollowClose(overlay, closeFollow);

  // Portal the drawer to <body> in live mode so its `position: fixed` panel escapes
  // any transformed / stacking-context ancestor. Skip in the theme editor so Shopify
  // block selection keeps working in place. Deferred one macrotask so inner product
  // cards / swipers initialise first (moving the node preserves their listeners).
  if (drawer && !window.Shopify?.designMode && drawer.parentElement !== document.body) {
    setTimeout(() => {
      document.body.appendChild(drawer);
      drawer.querySelectorAll<HTMLElement>('.js-justify-text').forEach(fitText);
    }, 0);
  }

  // De-dupe any drawers sharing this id (repeated portals / editor re-init).
  if (drawerId) {
    document
      .querySelectorAll<HTMLElement>(`.js-drawer[data-drawer-id="${drawerId}"]`)
      .forEach((el) => {
        if (el !== drawer) el.remove();
      });
  }

  const openDrawer = () => {
    document.documentElement.classList.add('overflow-hidden');
    closeBtn?.classList.remove('hidden');
    // Always start from the top of the editorial content on (re)open
    if (drawerScroll) drawerScroll.scrollTop = 0;
    panel?.classList.remove('translate-x-full');
    overlay?.classList.remove('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay?.classList.remove('opacity-0');
      });
    });
    header?.classList.add('opacity-0', 'pointer-events-none');
  };

  const closeDrawer = () => {
    document.documentElement.classList.remove('overflow-hidden');
    closeBtn?.classList.add('hidden');
    panel?.classList.add('translate-x-full');
    overlay?.classList.add('opacity-0');
    cursorClose.hide();
    setTimeout(() => {
      overlay?.classList.add('hidden');
    }, 250);
    header?.classList.remove('opacity-0', 'pointer-events-none');
  };

  const onOpenEvent = ((event: CustomEvent) => {
    if (!drawerId) return;
    if (event.detail?.id !== drawerId) return;
    openDrawer();
  }) as EventListener;

  document.addEventListener('drawer:open', onOpenEvent);

  bindAll(drawer, '[data-cta-type="close_drawer"]', 'click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);

  // Escape closes this drawer (only when it is the open one).
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !panel?.classList.contains('translate-x-full')) closeDrawer();
  };
  document.addEventListener('keydown', onKeydown);

  const shopifySection = drawer.closest('.shopify-section') as HTMLElement | null;

  return {
    unload() {
      document.removeEventListener('drawer:open', onOpenEvent);
      document.removeEventListener('keydown', onKeydown);
      cursorClose.destroy();
    },

    selectBlock(block) {
      openDrawer();
      if (shopifySection) shopifySection.style.zIndex = '999';

      requestAnimationFrame(() => {
        if (!drawerScroll) return;
        const top =
          block.getBoundingClientRect().top -
          drawerScroll.getBoundingClientRect().top +
          drawerScroll.scrollTop;

        drawerScroll.scrollTo({
          top: top - 120,
          behavior: 'smooth',
        });
      });

      return () => {
        closeDrawer();
        if (shopifySection) shopifySection.style.zIndex = '';
      };
    },
  };
});

// Delegated open trigger: any `[data-cta-type="open_drawer"]` with a
// `[data-drawer-target]` dispatches `drawer:open` for the matching drawer id.
document.addEventListener('click', (event) => {
  const trigger = (event.target as HTMLElement).closest(
    '[data-cta-type="open_drawer"]'
  ) as HTMLElement | null;
  if (!trigger) return;

  const id = trigger.dataset.drawerTarget;
  if (!id) return;

  document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
});
